import type { ItemFormData } from '@/api/items/adminItemMutations';
import {
  createAdminItemMutation,
  createAdminItemPriceMutation,
  deleteAdminItemMutation,
  deleteAdminItemPriceMutation,
  updateAdminItemMutation,
  updateAdminItemPriceMutation,
} from '@/api/items/adminItemMutations';
import {
  getAdminItemQuery,
  getAdminItemsQuery,
} from '@/api/items/adminItemQueries';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import type { Item } from '@/domain/Item';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { DeleteIngredientDialog } from './DeleteDupplementDialog';
import type { SupplementFilters as SupplementFiltersType } from './SupplementFilters';
import { SupplementFilters } from './SupplementFilters';
import { IngredientFormDialog } from './SupplementFormDialog';

const INITIAL_SUPPLEMENT_FILTERS: SupplementFiltersType = { procedure: 'add' };

export const Supplements = ({
  accordionOpenValue,
}: {
  accordionOpenValue?: string;
}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<SupplementFiltersType>(
    INITIAL_SUPPLEMENT_FILTERS,
  );
  const [page, setPage] = useState(1);
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  const handleAccordionOpen = useCallback(() => {
    setFilters(INITIAL_SUPPLEMENT_FILTERS);
    setPage(1);
    setFiltersResetKey((k) => k + 1);
  }, []);
  const [selectedSupplementId, setSelectedSupplementId] = useState<string>('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isViewAll = filters.procedure === 'view';
  const isEdit = filters.procedure === 'edit';
  const isAdd = filters.procedure === 'add';
  const isDelete = filters.procedure === 'delete';

  const { data: response, isLoading } = useQuery({
    ...getAdminItemsQuery(page),
    enabled: isViewAll || isEdit || isDelete,
  });

  const { data: itemDetails } = useQuery({
    ...getAdminItemQuery(Number(selectedSupplementId) || 0),
    enabled: isEdit && !!selectedSupplementId,
  });

  const items = response?.data || [];
  const meta = response?.meta;

  const itemOptions = items.map((i) => ({
    id: i.id.toString(),
    value: i.name,
    label: i.name,
  }));

  const createMutation = useMutation({
    ...createAdminItemMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminItems.all(),
      });
      setFormDialogOpen(false);
      toast.success('Supplement created successfully');
    },
  });

  const updateMutation = useMutation({
    ...updateAdminItemMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminItems.all(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminItems.single(Number(selectedSupplementId)),
      });
      setFormDialogOpen(false);
      setSelectedSupplementId('');
      toast.success('Supplement updated successfully');
    },
  });

  const deleteMutation = useMutation({
    ...deleteAdminItemMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminItems.all(),
      });
      setDeleteDialogOpen(false);
      setSelectedSupplementId('');
      toast.success('Supplement deleted successfully');
    },
  });

  const createPriceMutation = useMutation(createAdminItemPriceMutation());
  const updatePriceMutation = useMutation(updateAdminItemPriceMutation());
  const deletePriceMutation = useMutation(deleteAdminItemPriceMutation());

  const handleAddItem = () => {
    setFormDialogOpen(true);
  };

  const handleEditItem = () => {
    if (selectedSupplementId) {
      setFormDialogOpen(true);
    }
  };

  const handleDeleteItem = () => {
    if (selectedSupplementId) {
      setDeleteDialogOpen(true);
    }
  };

  const handleFormSubmit = async (data: ItemFormData) => {
    if (isAdd) {
      createMutation.mutate(data);
    } else {
      const itemId = Number(selectedSupplementId);

      // 1. Update main item data (excluding strengths to avoid conflicts if needed,
      // but let's keep it simple for now)
      updateMutation.mutate({
        ...data,
        id: itemId,
        strengths: undefined, // Don't send strengths in main update if using granular
      });

      // 2. Handle strengths via granular API
      if (data.strengths) {
        const promises = data.strengths.map((s) => {
          if (s.toDelete && s.id) {
            return deletePriceMutation.mutateAsync({ itemId, id: s.id });
          } else if (s.id) {
            return updatePriceMutation.mutateAsync({
              itemId,
              id: s.id,
              strength: s.strength,
              price: s.price,
              weight: s.weight ?? null,
              nrv_percent: s.nrv_percent ?? null,
            });
          } else if (!s.toDelete) {
            return createPriceMutation.mutateAsync({
              itemId,
              strength: s.strength,
              price: s.price,
              weight: s.weight ?? null,
              nrv_percent: s.nrv_percent ?? null,
            });
          }
          return Promise.resolve();
        });

        try {
          await Promise.all(promises);
          queryClient.invalidateQueries({
            queryKey: queryKeys.adminItems.single(itemId),
          });
        } catch (error) {
          console.error('Error updating strengths:', error);
          toast.error('Some strengths failed to update');
        }
      }
    }
  };

  const handleSaveStrength = async (
    itemId: number,
    strength: {
      id: string;
      strength: string;
      price: string;
      weight?: string;
      nrv_percent?: string;
    },
  ) => {
    const price = parseFloat(strength.price.replace(',', '.'));
    const parseOptional = (value?: string): number | null => {
      const trimmed = (value ?? '').trim().replace(',', '.');
      if (!trimmed) return null;
      const num = parseFloat(trimmed);
      return Number.isNaN(num) ? null : num;
    };
    const weight = parseOptional(strength.weight);
    const nrv_percent = parseOptional(strength.nrv_percent);
    const id = Number(strength.id);

    try {
      if (!isNaN(id) && id < 1000000000) {
        // Simple heuristic for existing id vs Date.now()
        await updatePriceMutation.mutateAsync({
          itemId,
          id,
          strength: strength.strength,
          price,
          weight,
          nrv_percent,
        });
        toast.success('Strength updated successfully');
      } else {
        await createPriceMutation.mutateAsync({
          itemId,
          strength: strength.strength,
          price,
          weight,
          nrv_percent,
        });
        toast.success('Strength created successfully');
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminItems.single(itemId),
      });
    } catch (error) {
      toast.error('Failed to save strength');
    }
  };

  const handleInlineDeleteStrength = async (itemId: number, id: number) => {
    try {
      await deletePriceMutation.mutateAsync({ itemId, id });
      toast.success('Strength deleted successfully');
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminItems.single(itemId),
      });
    } catch (error) {
      toast.error('Failed to delete strength');
    }
  };

  const handleDeleteConfirm = (supplementId: number) => {
    deleteMutation.mutate(supplementId);
  };

  const renderAddView = () => (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="primary" size="md" onClick={handleAddItem}>
          Add new supplement
        </Button>
      </div>
    </div>
  );

  const handleSupplementChange = (value: string | undefined) => {
    setSelectedSupplementId(value || '');
  };

  const renderEditView = () => (
    <div className="flex flex-col gap-4">
      <div className="w-full max-w-md">
        <Combobox
          id="search-supplement"
          label="Search for item"
          items={itemOptions}
          value={selectedSupplementId}
          onChange={handleSupplementChange}
        />
      </div>
      {selectedSupplementId && (
        <div>
          <RelationActionButton onClick={handleEditItem}>
            Edit item
          </RelationActionButton>
        </div>
      )}
    </div>
  );

  const renderDeleteView = () => (
    <div className="flex flex-col gap-4">
      <div className="w-full max-w-md">
        <Combobox
          id="search-supplement-delete"
          label="Search for item"
          items={itemOptions}
          value={selectedSupplementId}
          onChange={handleSupplementChange}
        />
      </div>
      {selectedSupplementId && (
        <div>
          <RelationActionButton onClick={handleDeleteItem}>
            Delete item
          </RelationActionButton>
        </div>
      )}
    </div>
  );

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    createPriceMutation.isPending ||
    updatePriceMutation.isPending ||
    deletePriceMutation.isPending;

  return (
    <>
      <DashboardTableAccordion
        value="supplements"
        title="Supplements"
        accordionOpenValue={accordionOpenValue}
        onOpen={handleAccordionOpen}
        data={isViewAll ? items : []}
        isLoading={isViewAll ? isLoading : false}
        loadingMessage="Loading supplements..."
        paginationMeta={isViewAll ? meta : undefined}
        onPageChange={setPage}
        summarySection={
          <div className="flex flex-col gap-4">
            <SupplementFilters
              key={filtersResetKey}
              filters={filters}
              onFiltersChange={(newFilters) => {
                setFilters(newFilters);
                setPage(1);
              }}
            />
            {isAdd && renderAddView()}
            {isEdit && renderEditView()}
            {isDelete && renderDeleteView()}
          </div>
        }
        renderHeader={() => {
          if (!isViewAll) return null;
          return (
            <>
              <TableHead>Supplement</TableHead>
            </>
          );
        }}
        renderRow={(item: Item) => {
          if (!isViewAll) return null;
          return (
            <>
              <TableCell>{item.name}</TableCell>
            </>
          );
        }}
      />

      <IngredientFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        supplement={isEdit ? itemDetails : null}
        mode={isAdd ? 'add' : 'edit'}
        onSubmit={handleFormSubmit}
        onSaveStrength={handleSaveStrength}
        onDeleteStrength={handleInlineDeleteStrength}
        isPending={isPending}
      />

      <DeleteIngredientDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        supplement={
          items.find((i) => i.id.toString() === selectedSupplementId) || null
        }
        onConfirm={handleDeleteConfirm}
        isPending={isPending}
      />
    </>
  );
};
